/** @jsx jsx */
import { jsx } from "@emotion/core"
import React from "react"
import { List } from "@cd2/components"
import { LabelInfo } from "../Components/LabelInfo"
import { IRecord } from "../types/schema"
import { formatPrice } from "../utils/utils"
import { HoverCustomerLoader } from "./HoverCustomerLoader"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { HoverUserLoader } from "./HoverUserLoader"
import { buildRoute, eRoutes } from "../routes"
import { getTotals } from "../utils/getQuoteOrderTotals"
import { useGlobalState } from "../Components/GlobalStateProvider"

type IQuote = IRecord<
  "quotes",
  "identifier" | "customer_reference" | "carriage_pennies" | "updated_at"
> & {
  customer: IRecord<
    "customers",
    "name" | "account_reference" | "cash_sales" | "default_sage_tax_rate_id" | "tax_rate"
  >
  items: IRecord<
    "quote_items",
    "type" | "price_pennies" | "quantity" | "body" | "stock_code" | "discountPercent"
  >[]
  user: IRecord<"skeem_users", "skeem_email" | "name">
}

interface IProps {
  id: string
  initialData?: IQuote
  renderInlineLink?: "block" | "inline"
  inline?: boolean
}

export function HoverQuoteLoader({ id, initialData, renderInlineLink, inline }: IProps) {
  const skeem = window.skeem
  const { currency } = useGlobalState()

  async function loadData() {
    const quote = await skeem.fetchOne<IQuote>({
      quotes: {
        attributes: [
          "identifier",
          "customer_reference",
          "carriage_pennies",
          "updated_at",
          {
            name: "customer",
            attributes: [
              "name",
              "account_reference",
              "cash_sales",
              "default_sage_tax_rate_id",
              "tax_rate",
            ],
          },
          { name: "user", attributes: ["skeem_email", "name"] },
          {
            name: "items",
            attributes: [
              "price_pennies",
              "markUp",
              "cost_price_pennies",
              "quantity",
              "discountPercent",
            ],
          },
        ],
        filter: { matches: { id } },
      },
    })
    return quote
  }

  return (
    <HoverResourceLoader
      reloadKey={id}
      loadData={loadData}
      inline={inline}
      initialData={initialData}
      viewLink={quote => buildRoute(eRoutes["/quotes/:id/preview"], { id: quote.id })}
      renderInlineLink={renderInlineLink}
      resourceName="Quote"
      renderHoverContentTitle={record => record.identifier}
      renderHoverContent={record => (
        <div>
          <List>
            <LabelInfo labelWidth={80} label="Identifier" value={record.identifier} inline />
            <LabelInfo
              labelWidth={80}
              label="Customer"
              value={<HoverCustomerLoader id={record.customer?.id} initialData={record.customer} />}
              inline
            />

            <LabelInfo labelWidth={80} label="Reference" value={record.customer_reference} inline />
            <LabelInfo
              labelWidth={80}
              label="Total Price"
              value={formatPrice(
                getTotals(record.items, record.carriage_pennies, record.customer?.tax_rate)
                  .totalPricePennies,
                { isPennies: true, currency },
              )}
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Quoted By"
              value={<HoverUserLoader id={record.user.id} initialData={record.user} />}
              inline
            />
          </List>
        </div>
      )}
      renderTarget={record => <span>{record.identifier}</span>}
    />
  )
}
